import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Modal from 'LuxKit/Modal/Modal'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { EmptyArray } from 'lib/array/arrayUtils'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import React, { useCallback, useContext, useMemo } from 'react'
import ExperimentalSearchTileFeaturesList from '../shared/ExperimentalSearchTileFeaturesList'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useSearchTileBedbankRate from 'hooks/Search/useSearchTileBedbankRate'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankCancellationPolicy from '../../../OfferPage/Bedbank/BedbankCancellationPolicy'
import useOffer from 'hooks/Offers/useOffer'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  searchTileBedbankRate: ReturnType<typeof useSearchTileBedbankRate>
}

function ExperimentalHotelSearchTileFeatures({
  bestPackage,
  filters,
  offer,
  offerMetaData,
  searchTileBedbankRate,
}: Props) {
  const metaData = useOfferMetaData(offer.id, filters)

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const { isBedbankRate, bedbankRateForRoom, isFetchingBedbankRates } = searchTileBedbankRate

  const [bedbankOffer] = useOffer<App.BedbankOffer>(isBedbankRate ? metaData?.pricing.ratePropertyId : undefined)

  const bedbankInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!bedbankRateForRoom?.inclusions?.length) return EmptyArray
    return bedbankRateForRoom.inclusions.filter((inclusion: App.OfferInclusion) => inclusion.description !== 'Room only')
  }, [bedbankRateForRoom?.inclusions])

  const featuresToDisplay = useMemo<{
    featureList: Array<App.OfferInclusion>
    featureType: 'inclusions' | 'amenities'
  }>(() => {
    if (bedbankInclusions.length > 0) {
      return { featureList: bedbankInclusions, featureType: 'inclusions' }
    }

    // Package inclusions describe the priced package, so they match the offer page's room option.
    // Regular and bonus inclusions are listed together, as on the offer page.
    if (inclusions.length > 0 || bonusInclusions.length > 0) {
      return { featureList: [...inclusions, ...bonusInclusions], featureType: 'inclusions' }
    }

    // Offer-level tile inclusions are marketing copy set per offer, not per package, so they are
    // only a fallback for packages that carry no inclusions of their own.
    if (offer.tileInclusions?.length) {
      return { featureList: offer.tileInclusions, featureType: 'inclusions' }
    }

    const amenities = offer.lowestPricePackage?.roomType.amenities ?? []
    if (amenities.length > 0) return { featureList: amenities, featureType: 'amenities' }

    return { featureList: EmptyArray, featureType: 'inclusions' }
  }, [bedbankInclusions, bonusInclusions, inclusions, offer.tileInclusions, offer.lowestPricePackage?.roomType.amenities])

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionText="View offer"
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        <OfferInclusionsList type="lux-plus" inclusions={luxPlusInclusions} />
        {featuresToDisplay && <OfferInclusionsList inclusions={featuresToDisplay.featureList} />}
      </Group>
    </Modal>)
  }, [luxPlusInclusions, offer, featuresToDisplay, offerUrl, showModal])

  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type

  const saveAmount = !!metaData?.pricing?.lowestMemberPrice && !!metaData?.pricing?.lowestPrice ?
    metaData?.pricing?.lowestPrice - metaData?.pricing?.lowestMemberPrice : undefined

  return (
    <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" gap={8} noDisplayWhenEmpty>
      {isBedbankRate && isFetchingBedbankRates && (
        <VerticalSpacer gap={4}>
          <TextLoadingBox typography="body-small" width="60%" />
          <TextLoadingBox typography="body-small" width="80%" />
          <TextLoadingBox typography="body-small" width="40%" />
        </VerticalSpacer>
      )}
      {!(isBedbankRate && isFetchingBedbankRates) && (
        <>
          <ExperimentalSearchTileFeaturesList
            features={featuresToDisplay.featureList}
            featuresType={featuresToDisplay.featureType}
            cancellationPolicy={
              <>
                {!!cancellationPolicyType && !isBedbankRate && (
                  <TileCancellationPolicy
                    icon={<LineCalendarIcon />}
                    cancellationPolicyType={cancellationPolicyType}
                    checkInDate={filters?.checkIn}
                    timezoneOffset={offer.property.timezoneOffset}
                    offerType={isBedbankRate ? 'bedbank_hotel' : offer.type}
                  />
                )}
                {isBedbankRate && bedbankOffer && bedbankRateForRoom && (
                  <BedbankCancellationPolicy
                    offer={bedbankOffer}
                    rate={bedbankRateForRoom}
                    showNonCancellation={false}
                    icon={<LineCalendarIcon />}
                  />
                )}
              </>
            }
            onMoreClick={openAllInclusionsModal}
          />

          {!!luxPlusInclusions?.length && (
            <LuxPlusLabelWithModal
              type="member-inclusions"
              inclusions={luxPlusInclusions}
              offer={offer}
              hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
            />
          )}
          {!!saveAmount && saveAmount > 0 && !luxPlusInclusions?.length && (
            <LuxPlusLabelWithModal
              type="member-price-offer"
              offer={offer}
              saveAmount={saveAmount}
            />
          )}
        </>
      )}
    </Group>
  )
}

export default ExperimentalHotelSearchTileFeatures
